/**
 * DigestSubscribeCard — small in-app opt-in surface for the weekly
 * shortlist digest. Signed-in users see a one-click subscribe button;
 * anonymous visitors get an email field.
 *
 * Renders three states:
 *   - Not subscribed: "Get weekly updates on your shortlist" + CTA
 *   - Subscribed:     "You're on the digest list" + unsubscribe link
 *   - Loading:        skeleton
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle2, Loader2, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function DigestSubscribeCard() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);   // { subscribed, sub }
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    api.get("/digest/subscribe/status")
      .then((r) => !cancelled && setStatus(r.data))
      .catch(() => !cancelled && setStatus({ subscribed: false }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [authLoading, user]);

  const subscribe = async () => {
    const target = user?.email || email.trim();
    if (!target || !target.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/digest/subscribe", {
        email: target, frequency: "weekly",
      });
      toast.success(data.message || "Subscribed");
      // Refresh status pill for signed-in users.
      if (user) {
        const s = await api.get("/digest/subscribe/status");
        setStatus(s.data);
      } else {
        setStatus({ subscribed: true, sub: { email: target } });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not subscribe");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div
        data-testid="digest-card-loading"
        className="rounded-lg border border-stone-200 bg-white p-4 h-24 animate-pulse"
      />
    );
  }
  if (status?.subscribed) {
    return (
      <div
        data-testid="digest-card-subscribed"
        className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-4"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <div className="font-editorial text-lg text-emerald-900 leading-snug">
              You're on the digest list
            </div>
            <p className="mt-1 text-[13px] text-stone-700 leading-relaxed">
              We'll email <span className="font-mono">{status.sub?.email}</span>{" "}
              when a university on your shortlist changes acceptance rate,
              tuition or graduate salary — no more than once a week. Manage
              from the footer of any digest email.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="digest-card-signup"
      className="rounded-lg border border-[#4C1D95] bg-[#4C1D95]/[0.03] p-5"
    >
      <div className="flex items-start gap-3">
        <Bell className="h-5 w-5 text-[#4C1D95] mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-editorial text-lg text-[#002147]">
            Weekly digest — tell me when things change
          </div>
          <p className="mt-1 text-[13px] text-stone-700 leading-relaxed">
            We monitor every university on your shortlist. When acceptance
            rate, international tuition or LEO/Scorecard graduate salary
            moves meaningfully, we email you a compact digest — no more
            than once a week, no marketing.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {!user && (
              <input
                data-testid="digest-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="text-[13px] rounded-md border border-stone-300 bg-white px-3 py-2 flex-1 min-w-[220px] focus:border-[#4C1D95] focus:outline-none"
              />
            )}
            <Button
              data-testid="digest-subscribe-btn"
              onClick={subscribe}
              disabled={busy}
              size="sm"
              className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
            >
              {busy ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Subscribing…</>
              ) : (
                <><Mail className="h-4 w-4 mr-1" /> Turn on digest</>
              )}
            </Button>
          </div>
          <div className="mt-2 text-[10px] font-underground-sc tracking-wider text-stone-500">
            One-click unsubscribe · We never sell your email
          </div>
        </div>
      </div>
    </div>
  );
}
